import { useStore } from '@nanostores/react';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { AppShell } from '@/app/layouts';
import { CareStateProvider } from '@/entities/care-state-domain';
import { useDemoCopyResetTransitions, useDemoResetEpoch } from '@/entities/sandbox-demo';
import { PatientEligibilityWarning } from '@/features/patient-eligibility-warning';
import { CareStateQuerySync } from '@/features/care-state-query-sync';
import { KarmaAwardToast } from '@/features/karma-award-feedback';
import {
  PatientGlobalSearchDialog,
  type PatientGlobalSearchHit,
} from '@/features/patient-global-search';
import { AgentTweakableStateProvider } from '@/features/copilot/agent-tweakable';
import { CopilotSessionProvider } from '@/features/copilot/lib';
import { selectPatientPanelTab } from '@/features/patient-workspace';
import { RequestsCompletionPrompt } from '@/features/requests-completion-prompt';
import { ResetDemoPatientOverlay } from '@/features/sandbox-demo';
import { ANALYTICS_EVENTS, analytics } from '@/shared';
import { PatientScopeProvider } from '@/shared/lib';
import {
  $faxEditorRequest,
  $referralEditorRequest,
  clearPatientWorkspaceSearchTarget,
  closeFaxEditor,
  closeReferralEditor,
  openFaxEditor,
  setPatientWorkspaceSearchTarget,
} from '@/shared/stores';
import { CopilotPanel } from '@/widgets/copilot-panel';
import { FaxEditorWorkspace } from '@/widgets/fax-editor';
import { PatientPanel } from '@/widgets/patient-panel';
import { ReferralEditorWorkspace } from '@/widgets/referral-editor';

import { PatientNavigatorContent } from './PatientNavigatorContent';
import {
  useOpenFollowUpsOnArrival,
  usePatientWorkspaceNavigation,
} from './patientWorkspaceNavigation';

function OpenFollowUpsOnArrival() {
  useOpenFollowUpsOnArrival();
  return null;
}

/**
 * Patient Workspace Page
 *
 * Composition-only page that assembles the patient workspace layout.
 * This page composes layouts and widgets — it contains NO domain logic,
 * stores, or data fetching. All business logic lives in features/entities.
 *
 * Structure:
 * - AppShell: Resizable two-panel split (no global header)
 *   - Left panel: PatientPanel widget (title, tabs, content)
 *   - Right panel: CopilotPanel widget (tabs, thread, composer)
 *   - Left overlay: PatientNavigator (hover sidebar)
 */
function PatientWorkspacePage() {
  const { userId } = useParams<{ userId: string }>();
  const resolvedUserId = userId ?? '';
  const demoResetEpoch = useDemoResetEpoch(resolvedUserId);
  // Owned here, above the epoch-keyed providers, because the fax editor swaps
  // PatientPanel (and the reset control with it) out of the left workspace
  // while a reset may still be in flight.
  useDemoCopyResetTransitions(resolvedUserId);
  const faxEditorRequest = useStore($faxEditorRequest);
  const referralEditorRequest = useStore($referralEditorRequest);
  const openedAtMsRef = useRef<number>(0);
  const [isGlobalSearchOpen, setIsGlobalSearchOpen] = useState(false);
  const { handleCloseFaxEditor, handleCloseReferralEditor } =
    usePatientWorkspaceNavigation(resolvedUserId);

  const handleOpenGlobalSearch = () => {
    if (resolvedUserId.trim() === '') {
      return;
    }
    setIsGlobalSearchOpen(true);
  };

  const requestedFaxEditor =
    faxEditorRequest?.patientId === resolvedUserId ? faxEditorRequest : null;

  useEffect(() => {
    if (faxEditorRequest !== null && faxEditorRequest.patientId !== resolvedUserId) {
      closeFaxEditor();
    }
  }, [faxEditorRequest, resolvedUserId]);

  const requestedReferralEditor =
    referralEditorRequest?.patientId === resolvedUserId ? referralEditorRequest : null;

  useEffect(() => {
    if (referralEditorRequest !== null && referralEditorRequest.patientId !== resolvedUserId) {
      closeReferralEditor();
    }
  }, [referralEditorRequest, resolvedUserId]);

  const handleSelectGlobalSearchHit = (hit: PatientGlobalSearchHit) => {
    selectPatientPanelTab(hit.tabId, { actor: 'clinician', patientUserId: resolvedUserId });
    if (hit.resultId !== undefined) {
      setPatientWorkspaceSearchTarget({
        tabId: hit.tabId,
        resource: hit.resource,
        query: hit.title,
        resultId: hit.resultId,
      });
      return;
    }
    clearPatientWorkspaceSearchTarget();
  };

  useEffect(() => {
    if (resolvedUserId.trim() === '') {
      return;
    }

    openedAtMsRef.current = Date.now();
    void analytics.track({
      name: ANALYTICS_EVENTS.workspace.patientOpened,
      context: {
        actor: 'clinician',
        patientUserId: resolvedUserId,
      },
    });

    return () => {
      const durationMs = Math.max(0, Date.now() - openedAtMsRef.current);
      void analytics.track({
        name: ANALYTICS_EVENTS.workspace.patientClosed,
        context: {
          actor: 'clinician',
          patientUserId: resolvedUserId,
        },
        properties: { durationMs },
      });
    };
  }, [resolvedUserId]);

  useEffect(() => {
    if (resolvedUserId.trim() === '') {
      return;
    }

    const onVisibilityChange = () => {
      const visibility = document.visibilityState === 'hidden' ? 'hidden' : 'visible';
      void analytics.track({
        name: ANALYTICS_EVENTS.workspace.visibilityChanged,
        context: {
          actor: 'clinician',
          patientUserId: resolvedUserId,
        },
        properties: { visibility },
      });
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [resolvedUserId]);

  useEffect(() => {
    const isEditableTarget = (target: EventTarget | null): boolean => {
      if (!(target instanceof HTMLElement)) {
        return false;
      }

      const tagName = target.tagName.toLowerCase();
      if (tagName === 'input' || tagName === 'textarea' || tagName === 'select') {
        return true;
      }

      if (target.isContentEditable) {
        return true;
      }

      return target.closest('[contenteditable="true"]') !== null;
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (resolvedUserId.trim() === '') {
        return;
      }
      if (event.key.toLowerCase() !== 'f') {
        return;
      }
      if (event.altKey || event.shiftKey) {
        return;
      }

      const isSearchShortcut = event.metaKey || event.ctrlKey;
      if (!isSearchShortcut) {
        return;
      }
      if (isEditableTarget(event.target)) {
        return;
      }

      event.preventDefault();
      setIsGlobalSearchOpen(true);
    };

    const options = { capture: true } as const;
    window.addEventListener('keydown', onKeyDown, options);
    return () => {
      window.removeEventListener('keydown', onKeyDown, options);
    };
  }, [resolvedUserId]);

  return (
    // Everything below remounts with fresh patient-scoped state when the
    // patient changes or a demo reset finishes.
    <PatientScopeProvider scopeKey={`${resolvedUserId || 'unknown-user'}:${demoResetEpoch}`}>
      <CareStateProvider patientUserId={resolvedUserId}>
        <OpenFollowUpsOnArrival />
        <CareStateQuerySync />
        <RequestsCompletionPrompt />
        <KarmaAwardToast />
        <PatientEligibilityWarning userId={resolvedUserId} />
        <AgentTweakableStateProvider>
          <CopilotSessionProvider userId={resolvedUserId}>
            <div className="relative h-screen w-screen">
              <AppShell
                persistenceId="patient-workspace"
                leftWorkspace={
                  requestedFaxEditor ? (
                    <FaxEditorWorkspace
                      key={requestedFaxEditor.requestId}
                      patientId={resolvedUserId}
                      draftId={requestedFaxEditor.draftId}
                      seed={requestedFaxEditor.seed}
                      onClose={handleCloseFaxEditor}
                    />
                  ) : requestedReferralEditor ? (
                    <ReferralEditorWorkspace
                      key={requestedReferralEditor.requestId}
                      patientId={resolvedUserId}
                      draftId={requestedReferralEditor.draftId}
                      onClose={handleCloseReferralEditor}
                    />
                  ) : (
                    <PatientPanel
                      userId={resolvedUserId}
                      onOpenGlobalSearch={handleOpenGlobalSearch}
                      globalSearchShortcutLabel="Cmd/Ctrl+F"
                      onCreateFax={() => openFaxEditor(resolvedUserId)}
                    />
                  )
                }
                rightCopilot={<CopilotPanel userId={resolvedUserId} />}
                leftOverlay={<PatientNavigatorContent activeUserId={userId} showFooter />}
              />
              <ResetDemoPatientOverlay patientUserId={resolvedUserId} />
            </div>
            <PatientGlobalSearchDialog
              open={isGlobalSearchOpen}
              onOpenChange={setIsGlobalSearchOpen}
              userId={resolvedUserId}
              onSelectHit={handleSelectGlobalSearchHit}
            />
          </CopilotSessionProvider>
        </AgentTweakableStateProvider>
      </CareStateProvider>
    </PatientScopeProvider>
  );
}

export default PatientWorkspacePage;
